import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { GBP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check } from "lucide-react";

const Field = ({ label, children }) => <div><Label>{label}</Label><div className="mt-1.5">{children}</div></div>;
const inp = "h-11 rounded-lg";

export function VoucherDialog({ slug, open, onOpenChange, accent, services = [] }) {
  const [f, setF] = useState({ amount: 50, purchaser_name: "", purchaser_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [mode, setMode] = useState("amount");
  const [serviceId, setServiceId] = useState("");
  const [busy, setBusy] = useState(false);
  const giftable = services.filter((s) => !s.is_addon && s.price_type !== "from" && s.price > 0);
  const svc = giftable.find((s) => s.id === serviceId);
  const price = mode === "service" ? (svc?.price || 0) : Number(f.amount);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const buy = async () => {
    setBusy(true);
    try {
      const body = { ...f, amount: mode === "amount" ? Number(f.amount) : null, service_id: mode === "service" ? serviceId : null, recipient_email: f.recipient_email || null, origin_url: window.location.origin };
      const { data } = await api.post(`/public/business/${slug}/voucher-checkout`, body);
      window.location.href = data.checkout_url;
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  const ok = (mode === "service" ? !!svc : f.amount >= 5) && f.purchaser_name && f.purchaser_email && f.recipient_name;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Buy a gift voucher</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-stone-100">
            {[["amount", "Money amount"], ["service", "A treatment"]].map(([k, l]) => (
              <button key={k} data-testid={`voucher-mode-${k}`} onClick={() => setMode(k)} className={`py-2 rounded-full text-sm font-semibold ${mode === k ? "bg-white shadow-sm" : "text-stone-500"}`}>{l}</button>
            ))}
          </div>
          {mode === "service" ? (
            <Field label="Treatment">
              <select data-testid="voucher-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)} className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm bg-white">
                <option value="">Choose a treatment</option>
                {[...new Set(giftable.map((s) => s.category))].map((c) => (
                  <optgroup key={c} label={c}>{giftable.filter((s) => s.category === c).map((s) => <option key={s.id} value={s.id}>{s.name} — {GBP(s.price)}</option>)}</optgroup>
                ))}
              </select>
            </Field>
          ) : (<>
          <div className="flex gap-2 flex-wrap">
            {[25, 50, 75, 100].map((a) => (
              <button key={a} data-testid={`voucher-amount-${a}`} onClick={() => setF((p) => ({ ...p, amount: a }))}
                className={`px-4 py-2 rounded-full border text-sm font-semibold ${Number(f.amount) === a ? "text-white border-transparent" : "bg-white border-stone-300"}`}
                style={Number(f.amount) === a ? { background: accent } : {}}>{GBP(a)}</button>
            ))}
          </div>
          <Field label="Or enter an amount (£)"><Input type="number" data-testid="voucher-amount" value={f.amount} onChange={set("amount")} className={inp} /></Field>
          </>)}
          <Field label="Your name"><Input data-testid="voucher-purchaser-name" value={f.purchaser_name} onChange={set("purchaser_name")} className={inp} /></Field>
          <Field label="Your email"><Input type="email" data-testid="voucher-purchaser-email" value={f.purchaser_email} onChange={set("purchaser_email")} className={inp} /></Field>
          <Field label="Recipient's name"><Input data-testid="voucher-recipient-name" value={f.recipient_name} onChange={set("recipient_name")} className={inp} /></Field>
          <Field label="Recipient's email (we'll send the e-voucher — leave blank to receive it yourself)"><Input type="email" data-testid="voucher-recipient-email" value={f.recipient_email} onChange={set("recipient_email")} className={inp} /></Field>
          <Field label="Message (optional)"><Textarea value={f.message} onChange={set("message")} rows={2} className="rounded-lg" /></Field>
          <Button data-testid="voucher-buy" disabled={!ok || busy} onClick={buy} className="w-full h-12 rounded-full text-white" style={{ background: accent }}>{busy ? "Please wait…" : `Pay ${GBP(price)}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CourseDialog({ slug, open, onOpenChange, accent, services, promo }) {
  const eligible = services.filter((s) => s.course_eligible);
  const min = promo?.min_sessions || 3;
  const [f, setF] = useState({ service_id: "", sessions: min, name: "", email: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const svc = eligible.find((s) => s.id === f.service_id);
  const full = svc ? svc.price * Number(f.sessions) : 0;
  const total = full * (1 - (promo?.percent || 0) / 100);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/business/${slug}/course-checkout`, { ...f, sessions: Number(f.sessions), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Buy a treatment course</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">{promo?.details}</p>
        <div className="space-y-2 mt-2">
          {eligible.map((s) => (
            <button key={s.id} data-testid={`course-service-${s.id}`} onClick={() => setF((p) => ({ ...p, service_id: s.id }))}
              className={`w-full text-left rounded-xl border p-3 text-sm flex justify-between ${f.service_id === s.id ? "border-2" : "border-stone-200"}`} style={f.service_id === s.id ? { borderColor: accent } : {}}>
              <span>{s.name}</span><span className="font-mono">{GBP(s.price)}</span>
            </button>
          ))}
        </div>
        <div className="space-y-3 mt-3">
          <Field label={`Number of sessions (min ${min})`}><Input type="number" min={min} data-testid="course-sessions" value={f.sessions} onChange={set("sessions")} className={inp} /></Field>
          <Field label="Your name"><Input data-testid="course-name" value={f.name} onChange={set("name")} className={inp} /></Field>
          <Field label="Email"><Input type="email" data-testid="course-email" value={f.email} onChange={set("email")} className={inp} /></Field>
          <Field label="Mobile"><Input data-testid="course-mobile" value={f.mobile} onChange={set("mobile")} className={inp} /></Field>
          {svc && (
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm" data-testid="course-total">
              <div className="flex justify-between"><span>{f.sessions} × {svc.name}</span><span className="line-through text-stone-400">{GBP(full)}</span></div>
              <div className="flex justify-between font-semibold mt-1"><span>Course price ({promo?.percent}% off)</span><span className="font-mono">{GBP(total)}</span></div>
              <p className="text-xs text-stone-500 mt-1">Paid in full now. Book each session online using the same email — it's covered by your course.</p>
            </div>
          )}
          <Button data-testid="course-buy" disabled={!svc || Number(f.sessions) < min || !f.name || !f.email || !f.mobile || busy} onClick={buy}
            className="w-full h-12 rounded-full text-white" style={{ background: accent }}>{busy ? "Please wait…" : `Pay ${GBP(total)}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EveningDialog({ slug, open, onOpenChange, accent, services }) {
  const [f, setF] = useState({ name: "", email: "", mobile: "", service_id: "", preferred_dates: "", notes: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const send = async () => {
    try { await api.post(`/public/business/${slug}/evening-request`, { ...f, service_id: f.service_id || null }); setSent(true); }
    catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Request an earlier or evening appointment</DialogTitle></DialogHeader>
        {sent ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="evening-sent"><Check className="h-4 w-4" /> Thanks! The salon will contact you to arrange a time.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Earlier and evening appointments are by request only. Tell us what you'd like and when suits you.</p>
            <Field label="Treatment">
              <select data-testid="evening-service" value={f.service_id} onChange={set("service_id")} className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm bg-white">
                <option value="">Not sure yet</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Preferred dates / times"><Input data-testid="evening-dates" value={f.preferred_dates} onChange={set("preferred_dates")} placeholder="e.g. Tue before 9am or Thu after 6pm" className={inp} /></Field>
            <Field label="Your name"><Input data-testid="evening-name" value={f.name} onChange={set("name")} className={inp} /></Field>
            <Field label="Email"><Input type="email" data-testid="evening-email" value={f.email} onChange={set("email")} className={inp} /></Field>
            <Field label="Mobile"><Input data-testid="evening-mobile" value={f.mobile} onChange={set("mobile")} className={inp} /></Field>
            <Field label="Notes (optional)"><Textarea value={f.notes} onChange={set("notes")} rows={2} className="rounded-lg" /></Field>
            <Button data-testid="evening-submit" disabled={!f.name || !f.email || !f.mobile} onClick={send} className="w-full h-12 rounded-full text-white" style={{ background: accent }}>Send request</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
